import { useCallback, useEffect, useState } from 'react';
import { listFolderContents, WorkDocsDocument, WorkDocsFolder } from 'src/services/WorkDocsService';
import { useWorkDocsAuth } from 'src/hooks/useWorkDocsAuth';
import { useIsMounted } from 'src/hooks/useIsMounted';
import { logger } from 'src/logger';

/** Cached contents of one folder plus its per-folder load/error state. */
export interface FolderNodeState {
  documents: WorkDocsDocument[];
  folders: WorkDocsFolder[];
  isLoading: boolean;
  error: string | null;
  loaded: boolean;
}

interface UseWorkDocsFolderBrowserArgs {
  /** The provisioned root folder id to browse from, or null if not yet provisioned. */
  rootFolderId: string | null;
}

interface UseWorkDocsFolderBrowserReturn {
  /** Set of folder ids the user has expanded. */
  expandedIds: Set<string>;
  /** Cached state (contents, loading, error) for every folder that has been listed. */
  nodeStateById: Map<string, FolderNodeState>;
  expandFolder: (folderId: string) => void;
  collapseFolder: (folderId: string) => void;
  /** Re-lists a folder, bypassing the cache. Awaitable so callers can act once the fresh listing lands. */
  refreshFolder: (folderId: string) => Promise<void>;
}

/**
 * Lazily browses a WorkDocs folder tree. Listing is deferred until a folder is first
 * expanded, then cached by folder id (re-expanding is instant). `refreshFolder` forces a
 * re-list, used after an upload lands a new document so it appears without a full reload.
 *
 * All listing is authenticated with the current WorkDocs access token. When the token or
 * root folder changes the cache and expansion state are reset.
 */
export const useWorkDocsFolderBrowser = ({ rootFolderId }: UseWorkDocsFolderBrowserArgs): UseWorkDocsFolderBrowserReturn => {
  const { accessToken } = useWorkDocsAuth();
  const isMounted = useIsMounted();

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [nodeStateById, setNodeStateById] = useState<Map<string, FolderNodeState>>(new Map());

  const loadFolder = useCallback(
    async (folderId: string) => {
      if (!accessToken) return;

      setNodeStateById((prev) => {
        const next = new Map(prev);
        const existing = next.get(folderId);
        next.set(folderId, {
          documents: existing?.documents ?? [],
          folders: existing?.folders ?? [],
          isLoading: true,
          error: null,
          loaded: existing?.loaded ?? false
        });
        return next;
      });

      try {
        const { documents, folders } = await listFolderContents(folderId, accessToken);
        if (!isMounted.current) return;
        setNodeStateById((prev) => {
          const next = new Map(prev);
          next.set(folderId, { documents, folders, isLoading: false, error: null, loaded: true });
          return next;
        });
      } catch (error) {
        if (!isMounted.current) return;
        logger.warn('Baseball Cards: failed to list WorkDocs folder', {
          folderId,
          error: error instanceof Error ? error.message : String(error)
        });
        setNodeStateById((prev) => {
          const next = new Map(prev);
          const existing = next.get(folderId);
          next.set(folderId, {
            documents: existing?.documents ?? [],
            folders: existing?.folders ?? [],
            isLoading: false,
            error: 'Could not load this folder. Try again.',
            loaded: existing?.loaded ?? false
          });
          return next;
        });
      }
    },
    [accessToken, isMounted]
  );

  // Reset browse state and re-open the root whenever the root folder or access token changes.
  // Keying on both means a token refresh (same root, new token) re-opens the tree cleanly, while
  // a manual collapse — which changes neither — is left alone (no runtime guard needed; the dep
  // array is the one-shot mechanism). loadFolder (not expandFolder) is used so the fresh listing
  // builds on the just-cleared cache via its functional setState, rather than reading a stale
  // pre-reset closure and skipping the reload on a same-root token refresh.
  useEffect(() => {
    setExpandedIds(rootFolderId ? new Set([rootFolderId]) : new Set());
    setNodeStateById(new Map());
    if (rootFolderId) {
      void loadFolder(rootFolderId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootFolderId, accessToken]);

  const expandFolder = useCallback(
    (folderId: string) => {
      setExpandedIds((prev) => new Set(prev).add(folderId));
      // List on first expand only; cached results are reused on re-expand.
      const state = nodeStateById.get(folderId);
      if (!state || (!state.loaded && !state.isLoading)) {
        void loadFolder(folderId);
      }
    },
    [nodeStateById, loadFolder]
  );

  const collapseFolder = useCallback((folderId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.delete(folderId);
      return next;
    });
  }, []);

  const refreshFolder = useCallback(
    (folderId: string) => {
      return loadFolder(folderId);
    },
    [loadFolder]
  );

  return { expandedIds, nodeStateById, expandFolder, collapseFolder, refreshFolder };
};
